// src/pages/auth/CompanyLoginPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import LanguageSwitcherAuth from '../../components/LanguageSwitcherAuth';
import AppVersionLabel from '../../components/common/AppVersionLabel';
import '../../styles/auth.css';

export default function CompanyLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loginMode, setLoginMode] = useState('password'); // 'password' | 'magiclink'
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [pendingMagicLinkCompletion, setPendingMagicLinkCompletion] = useState(false);

  const { 
    login, 
    sendMagicLink, 
    completeMagicLinkSignIn, 
    checkMagicLinkInUrl 
  } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('auth');

  // On mount: check if the current URL is a magic link redirect
  useEffect(() => {
    if (!checkMagicLinkInUrl()) return;

    const savedEmail = window.localStorage.getItem('emailForSignIn');

    if (savedEmail) {
      // Same device: complete sign-in automatically
      setLoading(true);
      completeMagicLinkSignIn(savedEmail)
        .then(() => {
          window.localStorage.removeItem('emailForSignIn');
          navigate('/admin/dashboard');
        })
        .catch((err) => {
          console.error(err);
          setError(t('messages.magicLinkExpired'));
          setLoading(false);
        });
    } else {
      // Different device: ask user to confirm their email
      setLoginMode('magiclink');
      setPendingMagicLinkCompletion(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function switchLoginMode(mode) {
    setLoginMode(mode);
    setMagicLinkSent(false);
    setError('');
  }

  async function handleSubmit(e) {
    e.preventDefault();

    try {
      setError('');
      setLoading(true);

      if (loginMode === 'magiclink') {
        if (pendingMagicLinkCompletion) {
          // Complete sign-in for user on a different device
          await completeMagicLinkSignIn(email);
          window.localStorage.removeItem('emailForSignIn');
          navigate('/admin/dashboard');
        } else {
          // Send the magic link
          await sendMagicLink(email);
          window.localStorage.setItem('emailForSignIn', email);
          setMagicLinkSent(true);
        }
      } else {
        // Password login
        await login(email, password);
        navigate('/admin/dashboard');
      }
    } catch (err) {
      console.error(err);
      if (loginMode === 'magiclink') {
        setError(
          pendingMagicLinkCompletion
            ? t('messages.magicLinkExpired')
            : t('messages.magicLinkError')
        );
      } else {
        setError(t('messages.loginError'));
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-container">
      <LanguageSwitcherAuth />
      <div className="auth-form-container">

        <div className="auth-logo-container">
          <img
            src="/48x48.png"
            srcSet="/48x48.png 1x, /96x96.png 2x, /144x144.png 3x"
            alt={t('alt.klokifyLogo')}
            className="auth-logo"
          />
          <h1 className="auth-brand-name">Klokify</h1>
        </div>

        <h2>{t('titles.companyLogin')}</h2>

        {error && <div className="auth-error">{error}</div>}

        {/* Mode toggle - hidden when completing sign-in on a different device */}
        {!pendingMagicLinkCompletion && (
          <div className="auth-mode-toggle">
            <button
              type="button"
              className={`auth-mode-button ${loginMode === 'password' ? 'active' : ''}`}
              onClick={() => switchLoginMode('password')}
            >
              {t('buttons.passwordLogin')}
            </button>
            <button
              type="button"
              className={`auth-mode-button ${loginMode === 'magiclink' ? 'active' : ''}`}
              onClick={() => switchLoginMode('magiclink')}
            >
              {t('buttons.magicLinkLogin')}
            </button>
          </div>
        )}

        {/* Info shown when user opened the magic link on a different device */}
        {pendingMagicLinkCompletion && (
          <div className="auth-info-box">
            {t('messages.magicLinkPendingCompletion')}
          </div>
        )}

        {/* Magic link sent confirmation state */}
        {magicLinkSent ? (
          <div className="auth-magic-link-sent">
            <div className="auth-success">{t('messages.magicLinkSent')}</div>
            <button
              type="button"
              className="auth-link-button"
              onClick={() => { setMagicLinkSent(false); setEmail(''); }}
            >
              {t('links.useDifferentEmail')}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="email">{t('labels.email')}</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            {loginMode === 'password' && (
              <div className="form-group">
                <label htmlFor="password">{t('labels.password')}</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
            )}

            <button type="submit" className="auth-button" disabled={loading}>
              {loginMode === 'magiclink'
                ? pendingMagicLinkCompletion
                  ? loading ? t('buttons.loggingIn') : t('buttons.completeSignIn')
                  : loading ? t('buttons.sendingLink') : t('buttons.sendMagicLink')
                : loading ? t('buttons.loggingIn') : t('buttons.login')
              }
            </button>
          </form>
        )}

        <div className="auth-links">
          {loginMode === 'password' && !pendingMagicLinkCompletion && (
            <Link to="/forgot-password">{t('links.forgotPassword')}</Link>
          )}
          {!pendingMagicLinkCompletion && (
            <Link to="/register">{t('links.registerCompany')}</Link>
          )}
          {pendingMagicLinkCompletion && (
            <button
              type="button"
              className="auth-link-button"
              onClick={() => {
                setPendingMagicLinkCompletion(false);
                setLoginMode('password');
                setError('');
              }}
            >
              {t('links.backToLogin')}
            </button>
          )}
        </div>

      </div>

      <AppVersionLabel className="auth-version-footer" />
    </div>
  );
}